/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import ExcelJS from "exceljs";
import PropTypes from "prop-types";
import { EditorState } from "draft-js";

import { simpleFetch } from "../../restWrapper";
import { PLATFORM } from "../../config";
import {
  descriptionCodeFrom,
  copyToClipboard,
} from "../../assets/projectContentHelper.js";
import TextBox from "../CreateGoal/TextBox";
import {
  downloadProjectTemplate,
  buildProjectHeaders,
} from "./projectTemplate";

import "./ProjectBulkImport.css";

const MAX_PREVIEW_ROWS = 5;
const MAX_ROWS = 1000;
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

const FALLBACKS = {
  back: "Back",
  title: "Import Projects",
  betaBadge: "Beta",
  intro: "Create many projects at once using an Excel template. Expected columns: {{columns}}. Up to 1000 projects per upload.",
  guideHeading: "How to fill in the template",
  guideRequired: "Only Title (Default) and Description (Default) are required — every other column is optional, and a blank cell is skipped.",
  guideDropdowns: "Type, Fee Coverage and Status are dropdowns — all optional. Leave a cell blank to use its default (Type → Donation, Fee Coverage → Default, Status → Open), or pick a value to override.",
  guideDropdownsNoType: "Fee Coverage and Status are dropdowns — both optional. Leave a cell blank to use its default (Fee Coverage → Default, Status → Open), or pick a value to override.",
  guideDescriptionFormat: "Description Format applies to every Description cell in that row, and is optional. Leave it blank (or pick Plain) to import the descriptions as text, exactly as before. Pick Formatted when the cells hold description code copied from a project's description editor — that keeps bold, headings and lists.",
  guideImageId: "Image ID: open Manage Images (in the Project Manager toolbar) to find each image's number. Enter one id, or several separated by commas — the images appear in the order you list them, e.g. \"1, 2, 3\" shows image 1 first. Up to 6 per project; extra ids are ignored.",
  formatterHeading: "Description formatter",
  formatterToggleShow: "Open formatter",
  formatterToggleHide: "Hide formatter",
  formatterIntro: "Write a description here and format it with the controls below. Copy the code it produces, paste it into a Description cell in the template, and set that row's Description Format to Formatted.",
  formatterPlaceholder: "Write the description you want to format here...",
  formatterCopy: "Copy description code",
  formatterCopied: "Copied — paste it into a Description cell.",
  formatterClear: "Clear",
  formatterEmpty: "Write a description first.",
  formatterCopyFailed: "Could not copy automatically. Select the code below and copy it manually.",
  premiumLocked: "Bulk project import requires a Premium subscription.",
  downloadTemplate: "Download template (.xlsx)",
  chooseFile: "Choose file (.xlsx / .csv)",
  chooseDifferentFile: "Choose a different file",
  clear: "Clear",
  rowsLabel: "{{n}} row(s)",
  previewHeader: "Previewing first {{n}} of {{total}} row(s).",
  previewRowHeader: "Row",
  importing: "Importing...",
  importBtn: "Import {{n}} project(s)",
  lastUploadBanner: "Last import: {{filename}} — {{count}} project(s) on {{when}}{{by}}. Make sure this is not a duplicate.",
  lastUploadBy: " by {{email}}",
  lastUploadUnknownFile: "(unknown file)",
  errFailedToGenerateTemplate: "Failed to generate template.",
  errFileTooLarge: "File too large ({{size}} MB). Maximum 5 MB.",
  errUnsupportedType: "Unsupported file type. Use .xlsx or .csv.",
  errTooManyRows: "File has {{n}} rows; maximum is {{max}} per upload.",
  errCouldNotRead: "Could not read the file. Is it a valid Excel/CSV?",
  errUploadFailed: "Upload failed.",
  errUploadFailedRetry: "Upload failed. Please try again.",
  errImportInProgress: "This import is still being processed. Wait a moment, then press Import again to see its result.",
  errImportNeedsReview: "A previous attempt of this import did not finish. Check your project list for partially imported projects before importing again.",
  resultIdempotent: "This batch was already imported. ",
  resultCreated: "Created {{created}} of {{total}} projects.",
  resultFailed: " {{n}} row(s) failed.",
  downloadErrorReport: "Download error report",
  errorRowPrefix: "Row {{row}}:",
  errorMore: "...and {{n}} more",
};

const interpolate = (str, vars) => {
  if (!str || !vars) return str;
  let out = str;
  Object.keys(vars).forEach((k) => {
    out = out.replace(new RegExp("\\{\\{" + k + "\\}\\}", "g"), vars[k]);
  });
  return out;
};

const generateBatchToken = () => {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return "batch-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
};

// The batchToken is what lets the backend recognize a retry of the same
// import. Keeping it in sessionStorage means the natural "it failed, reload
// the page and try again" flow reuses the token — the backend then answers
// from the batch record instead of importing the whole file a second time.
// sessionStorage access can throw (private browsing), so both helpers
// degrade to an in-memory token.
const BATCH_TOKEN_STORAGE_KEY = "ProjectBulkImport.batchToken";

const loadBatchToken = () => {
  try {
    const saved = window.sessionStorage.getItem(BATCH_TOKEN_STORAGE_KEY);
    if (saved) return saved;
  } catch (e) { /* fall through */ }
  return rotateBatchToken();
};

const rotateBatchToken = () => {
  const token = generateBatchToken();
  try {
    window.sessionStorage.setItem(BATCH_TOKEN_STORAGE_KEY, token);
  } catch (e) { /* keep the in-memory token */ }
  return token;
};

const splitCsvLine = (line) => {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else cur += c;
    } else {
      if (c === ",") { out.push(cur); cur = ""; }
      else if (c === '"') inQuotes = true;
      else cur += c;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim());
};

// Exceljs returns a variety of shapes for cell values. Unwrap hyperlink,
// rich text, formula, and error objects into plain scalars so we never
// render "[object Object]".
const extractCellValue = (raw) => {
  if (raw == null) return null;
  if (raw instanceof Date) return raw;
  if (typeof raw !== "object") return raw;
  if (Array.isArray(raw.richText)) {
    return raw.richText.map((r) => (r && r.text) || "").join("");
  }
  if (typeof raw.text === "string") return raw.text;
  if (raw.hyperlink && typeof raw.hyperlink === "string") {
    return raw.hyperlink.replace(/^mailto:/i, "");
  }
  if (raw.result !== undefined) return raw.result;
  return "";
};

// A "Formatted" Description cell holds serialized Draft.js content, which is
// kilobytes of JSON scaffolding around a little text. Show the text it will
// actually import instead, so the preview table stays readable. Plain cells
// fail the parse and fall through unchanged.
const draftPreviewText = (str) => {
  if (str.length < 2 || str[0] !== "{") return null;
  try {
    const parsed = JSON.parse(str);
    if (!parsed || !Array.isArray(parsed.blocks)) return null;
    return parsed.blocks
      .map((b) => (b && typeof b.text === "string" ? b.text : ""))
      .join("\n");
  } catch (e) {
    return null;
  }
};

const formatPreviewCell = (value) => {
  const v = extractCellValue(value);
  if (v == null) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 16).replace("T", " ");
  const str = String(v);
  const decoded = draftPreviewText(str);
  return decoded == null ? str : decoded;
};

const parseCsvText = (text) => {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = splitCsvLine(lines[0]);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = splitCsvLine(lines[i]);
    const obj = { _rowNumber: i + 1 };
    headers.forEach((h, j) => { if (h) obj[h] = values[j] == null ? "" : values[j]; });
    rows.push(obj);
  }
  return { headers, rows };
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString();
};

class ProjectBulkImport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      file: null,
      fileName: "",
      preview: [],
      previewHeaders: [],
      parsedRowCount: 0,
      submitting: false,
      error: "",
      parseError: "",
      result: null,
      lastUpload: null,
      batchToken: loadBatchToken(),
      formatterOpen: false,
      formatterState: EditorState.createEmpty(),
      formatterStatus: "",
      formatterCode: "",
    };
    this.fileInputRef = React.createRef();
  }

  t = (key, vars) => {
    const block =
      this.props.languageObj && typeof this.props.languageObj === "object"
        ? this.props.languageObj
        : {};
    return interpolate(block[key] || FALLBACKS[key] || "", vars);
  };

  isPremium = () => {
    const { organizationObj } = this.props;
    return !!(
      organizationObj &&
      organizationObj.currentPlan &&
      organizationObj.currentPlan.toLowerCase() === "premium"
    );
  };

  componentDidMount() {
    if (this.isPremium()) {
      this.fetchLastUpload();
    }
  }

  fetchLastUpload = () => {
    simpleFetch("/projects/bulk-upload/last", "GET")
      .then((res) => {
        if (res && res.status === 200 && res.body && res.body.lastUpload) {
          this.setState({ lastUpload: res.body.lastUpload });
        }
      })
      .catch(() => {});
  };

  handleBack = () => {
    this.props.history.push("/AccountOverview");
  };

  toggleFormatter = () => {
    this.setState((s) => ({ formatterOpen: !s.formatterOpen }));
  };

  handleFormatterChange = (formatterState) => {
    // Editing invalidates whatever was last copied, so drop the status.
    this.setState({ formatterState, formatterStatus: "", formatterCode: "" });
  };

  handleClearFormatter = () => {
    this.setState({
      formatterState: EditorState.createEmpty(),
      formatterStatus: "",
      formatterCode: "",
    });
  };

  handleCopyFormatted = async () => {
    const code = descriptionCodeFrom(this.state.formatterState);
    if (!code) {
      this.setState({ formatterStatus: "empty", formatterCode: "" });
      return;
    }
    const copied = await copyToClipboard(code);
    // No clipboard API (it needs a secure context) — fall back to showing the
    // code so it can be selected and copied by hand.
    this.setState({
      formatterStatus: copied ? "copied" : "failed",
      formatterCode: copied ? "" : code,
    });
  };

  handleDownloadTemplate = async () => {
    try {
      const { organizationObj } = this.props;
      await downloadProjectTemplate({
        organizationObj,
        includeType: PLATFORM === "checkplus",
      });
    } catch (err) {
      this.setState({ error: this.t("errFailedToGenerateTemplate") });
    }
  };

  handleFileSelect = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (file.size > MAX_SIZE_BYTES) {
      this.setState({
        parseError: this.t("errFileTooLarge", { size: (file.size / 1024 / 1024).toFixed(1) }),
        file: null,
        fileName: "",
        preview: [],
        parsedRowCount: 0,
      });
      return;
    }

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["xlsx", "csv"].includes(ext)) {
      this.setState({
        parseError: this.t("errUnsupportedType"),
        file: null,
        fileName: "",
        preview: [],
        parsedRowCount: 0,
      });
      return;
    }

    try {
      const { headers, rows } = await this.parseForPreview(file, ext);
      if (rows.length > MAX_ROWS) {
        this.setState({
          parseError: this.t("errTooManyRows", { n: rows.length, max: MAX_ROWS }),
          file: null,
          fileName: "",
          preview: [],
          parsedRowCount: 0,
        });
        return;
      }
      this.setState({
        file,
        fileName: file.name,
        preview: rows.slice(0, MAX_PREVIEW_ROWS),
        previewHeaders: headers,
        parsedRowCount: rows.length,
        parseError: "",
        error: "",
        result: null,
      });
    } catch (err) {
      this.setState({
        parseError: this.t("errCouldNotRead"),
        file: null,
        fileName: "",
        preview: [],
        parsedRowCount: 0,
      });
    }
  };

  parseForPreview = async (file, ext) => {
    if (ext === "csv") {
      const text = await file.text();
      return parseCsvText(text);
    }

    const workbook = new ExcelJS.Workbook();
    const buf = await file.arrayBuffer();
    await workbook.xlsx.load(buf);
    const sheet = workbook.worksheets[0];
    const rows = [];
    let headers = [];
    sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      const values = row.values.slice(1);
      if (rowNumber === 1) {
        headers = values.map((v) => {
          const unwrapped = extractCellValue(v);
          return unwrapped == null ? "" : String(unwrapped).trim();
        });
        return;
      }
      const obj = { _rowNumber: rowNumber };
      headers.forEach((h, i) => {
        if (!h) return;
        obj[h] = extractCellValue(values[i]);
      });
      rows.push(obj);
    });
    return { headers, rows };
  };

  handleClearFile = () => {
    this.setState({
      file: null,
      fileName: "",
      preview: [],
      parsedRowCount: 0,
      parseError: "",
      error: "",
      result: null,
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  handleSubmit = async () => {
    const { file, batchToken } = this.state;
    if (!file) return;

    this.setState({ submitting: true, error: "" });

    const formData = new FormData();
    formData.append("file", file);
    formData.append("batchToken", batchToken);

    try {
      const res = await simpleFetch(
        "/projects/bulk-upload",
        "POST",
        formData,
        true
      );
      if (res && res.status === 200 && res.body) {
        this.setState({
          result: res.body,
          submitting: false,
          batchToken: rotateBatchToken(),
        });
        this.fetchLastUpload();
      } else if (res && res.status === 409 && res.body) {
        // The same token is already claimed on the server. Keep the token:
        // once the original run finishes, retrying replays its result
        // instead of importing again.
        const key = res.body.inProgress
          ? "errImportInProgress"
          : "errImportNeedsReview";
        this.setState({ error: this.t(key), submitting: false });
      } else {
        const msg = (res && res.body && res.body.message) || this.t("errUploadFailed");
        this.setState({ error: msg, submitting: false });
      }
    } catch (err) {
      this.setState({ error: this.t("errUploadFailedRetry"), submitting: false });
    }
  };

  renderLastUploadBanner() {
    const { lastUpload } = this.state;
    if (!lastUpload) return null;
    return (
      <div className="ProjectBulkImport-banner">
        {this.t("lastUploadBanner", {
          filename: lastUpload.filename || this.t("lastUploadUnknownFile"),
          count: lastUpload.createdCount,
          when: formatDateTime(lastUpload.createdAt),
          by: lastUpload.createdByEmail
            ? this.t("lastUploadBy", { email: lastUpload.createdByEmail })
            : "",
        })}
      </div>
    );
  }

  renderPreview() {
    const { preview, previewHeaders, parsedRowCount } = this.state;
    if (!preview.length) return null;
    const visibleHeaders = previewHeaders.filter(Boolean);
    return (
      <div className="ProjectBulkImport-preview">
        <div className="ProjectBulkImport-previewHeader">
          {this.t("previewHeader", { n: preview.length, total: parsedRowCount })}
        </div>
        <div className="ProjectBulkImport-previewTableWrap">
          <table className="ProjectBulkImport-previewTable">
            <thead>
              <tr>
                <th>{this.t("previewRowHeader")}</th>
                {visibleHeaders.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((row) => (
                <tr key={row._rowNumber}>
                  <td>{row._rowNumber}</td>
                  {visibleHeaders.map((h) => (
                    <td key={h}>
                      {formatPreviewCell(row[h])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  renderResult() {
    const { result } = this.state;
    if (!result) return null;
    const hasErrors = result.failed > 0;
    return (
      <div
        className={
          "ProjectBulkImport-result " +
          (hasErrors
            ? "ProjectBulkImport-result--mixed"
            : "ProjectBulkImport-result--success")
        }
      >
        <div className="ProjectBulkImport-resultHeader">
          {result.idempotentReplay ? this.t("resultIdempotent") : ""}
          {this.t("resultCreated", { created: result.created, total: result.totalRows })}
          {hasErrors ? this.t("resultFailed", { n: result.failed }) : null}
        </div>
        {hasErrors && result.errorReportUrl ? (
          <div className="ProjectBulkImport-resultActions">
            <a href={result.errorReportUrl} target="_blank" rel="noreferrer">
              {this.t("downloadErrorReport")}
            </a>
          </div>
        ) : null}
        {hasErrors && result.failedPreview && result.failedPreview.length > 0 ? (
          <ul className="ProjectBulkImport-errorList">
            {result.failedPreview.map((e) => (
              <li key={e.row}>
                {this.t("errorRowPrefix", { row: e.row })} {e.errors.join("; ")}
              </li>
            ))}
            {result.failed > result.failedPreview.length ? (
              <li>{this.t("errorMore", { n: result.failed - result.failedPreview.length })}</li>
            ) : null}
          </ul>
        ) : null}
      </div>
    );
  }

  renderFormatter() {
    const { formatterOpen, formatterState, formatterStatus, formatterCode } =
      this.state;
    return (
      <div className="ProjectBulkImport-formatter">
        <button
          type="button"
          className="ProjectBulkImport-formatterToggle"
          onClick={this.toggleFormatter}
          aria-expanded={formatterOpen}
        >
          <span className="ProjectBulkImport-formatterHeading">
            {this.t("formatterHeading")}
          </span>
          <span className="ProjectBulkImport-formatterToggleHint">
            {formatterOpen
              ? this.t("formatterToggleHide")
              : this.t("formatterToggleShow")}
          </span>
        </button>

        {formatterOpen ? (
          <div className="ProjectBulkImport-formatterBody">
            <p className="ProjectBulkImport-formatterIntro">
              {this.t("formatterIntro")}
            </p>

            <TextBox
              descriptionContent={formatterState}
              handleDescription={this.handleFormatterChange}
              placeholder={this.t("formatterPlaceholder")}
            />

            <div className="ProjectBulkImport-formatterActions">
              <button
                type="button"
                className="ProjectBulkImport-btn ProjectBulkImport-btn--secondary"
                onClick={this.handleCopyFormatted}
              >
                {this.t("formatterCopy")}
              </button>
              <button
                type="button"
                className="ProjectBulkImport-linkBtn"
                onClick={this.handleClearFormatter}
              >
                {this.t("formatterClear")}
              </button>
              {formatterStatus === "copied" ? (
                <span className="ProjectBulkImport-formatterCopied">
                  {this.t("formatterCopied")}
                </span>
              ) : null}
              {formatterStatus === "empty" ? (
                <span className="ProjectBulkImport-formatterHint">
                  {this.t("formatterEmpty")}
                </span>
              ) : null}
            </div>

            {formatterStatus === "failed" ? (
              <div>
                <div className="ProjectBulkImport-error">
                  {this.t("formatterCopyFailed")}
                </div>
                <textarea
                  className="ProjectBulkImport-formatterCode"
                  readOnly
                  rows={4}
                  value={formatterCode}
                  onFocus={(e) => e.target.select()}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  renderHeader() {
    // When embedded in another page (CreateGoal tabs), the host provides
    // its own navigation and title.
    if (this.props.embedded) return null;
    return (
      <div className="ProjectBulkImport-header">
        <button
          type="button"
          className="ProjectBulkImport-backBtn"
          onClick={this.handleBack}
        >
          {"< " + this.t("back")}
        </button>
        <h2>
          {this.t("title")}{" "}
          <span className="ProjectBulkImport-betaBadge">{this.t("betaBadge")}</span>
        </h2>
      </div>
    );
  }

  render() {
    const {
      fileName,
      preview,
      parsedRowCount,
      submitting,
      error,
      parseError,
      result,
    } = this.state;

    if (!this.isPremium()) {
      return (
        <div className="ProjectBulkImport">
          {this.renderHeader()}
          <div className="ProjectBulkImport-locked">
            {this.t("premiumLocked")}
          </div>
        </div>
      );
    }

    const columns = buildProjectHeaders(PLATFORM === "checkplus").join(", ");

    return (
      <div className="ProjectBulkImport">
        {this.renderHeader()}

        <p className="ProjectBulkImport-intro">
          {this.t("intro", { columns })}
        </p>

        <div className="ProjectBulkImport-guide">
          <div className="ProjectBulkImport-guideHeading">
            {this.t("guideHeading")}
          </div>
          <ul className="ProjectBulkImport-guideList">
            <li>{this.t("guideRequired")}</li>
            <li>
              {PLATFORM === "checkplus"
                ? this.t("guideDropdowns")
                : this.t("guideDropdownsNoType")}
            </li>
            <li>{this.t("guideDescriptionFormat")}</li>
            <li>{this.t("guideImageId")}</li>
          </ul>
        </div>

        {this.renderFormatter()}

        {this.renderLastUploadBanner()}

        <div className="ProjectBulkImport-actions">
          <button
            type="button"
            className="ProjectBulkImport-btn ProjectBulkImport-btn--secondary"
            onClick={this.handleDownloadTemplate}
            disabled={submitting}
          >
            {this.t("downloadTemplate")}
          </button>

          <label
            className={
              "ProjectBulkImport-btn ProjectBulkImport-btn--primary " +
              (submitting ? "ProjectBulkImport-btn--disabled" : "")
            }
          >
            {fileName ? this.t("chooseDifferentFile") : this.t("chooseFile")}
            <input
              ref={this.fileInputRef}
              type="file"
              accept=".xlsx,.csv"
              onChange={this.handleFileSelect}
              disabled={submitting}
              style={{ display: "none" }}
            />
          </label>

          {fileName ? (
            <div className="ProjectBulkImport-fileInfo">
              <span>{fileName}</span> ({this.t("rowsLabel", { n: parsedRowCount })})
              <button
                type="button"
                className="ProjectBulkImport-linkBtn"
                onClick={this.handleClearFile}
                disabled={submitting}
              >
                {this.t("clear")}
              </button>
            </div>
          ) : null}
        </div>

        {parseError ? (
          <div className="ProjectBulkImport-error">{parseError}</div>
        ) : null}

        {preview.length ? this.renderPreview() : null}

        {preview.length && !result ? (
          <div className="ProjectBulkImport-commit">
            <button
              type="button"
              className="ProjectBulkImport-btn ProjectBulkImport-btn--primary ProjectBulkImport-submit"
              onClick={this.handleSubmit}
              disabled={submitting}
            >
              {submitting
                ? this.t("importing")
                : this.t("importBtn", { n: parsedRowCount })}
            </button>

            {error ? <div className="ProjectBulkImport-error">{error}</div> : null}
          </div>
        ) : null}

        {this.renderResult()}
      </div>
    );
  }
}

ProjectBulkImport.propTypes = {
  organizationObj: PropTypes.object,
  language: PropTypes.string,
  languageObj: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  history: PropTypes.object,
  embedded: PropTypes.bool,
};

export default ProjectBulkImport;
